import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageBanner from "../../components/PageBanner/PageBanner";
import PostCard from "../../components/postcard/PostCard";
import Sidebar from "../../components/sidebar/SideBar";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { postsService, WordPressPost } from "../../api/api.service";
import "./BlogPage.css";

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get("search");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const params: any = { per_page: 5 };
        if (searchTerm) {
          params.search = searchTerm;
        }
        const data = await postsService.getPosts(params);

        // Client-side filtering to ensure strict title matching as requested
        let filteredPosts = data;
        if (searchTerm) {
          const lowerTerm = searchTerm.toLowerCase();
          filteredPosts = data.filter((post: WordPressPost) =>
            post.title.rendered.toLowerCase().includes(lowerTerm)
          );
        }

        setPosts(filteredPosts);
      } catch (err) {
        setError("Blogs Unavailable Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();

    // Auto-refresh every 5 minutes as per requirements
    const interval = setInterval(fetchPosts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [searchTerm]);

  // Helper to extract clean data for PostCard
  const transformPostData = (post: WordPressPost) => {
    const featureMedia = post._embedded?.["wp:featuredmedia"]?.[0];
    const authorData = post._embedded?.author?.[0];

    // Strip HTML from excerpt
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = post.excerpt.rendered;
    const cleanExcerpt = tempDiv.textContent || tempDiv.innerText || "";

    return {
      id: post.id,
      image: postsService.getMediaBaseUrl(featureMedia),
      title: post.title.rendered,
      author: authorData?.name || "Unknown Author",
      authorImage: authorData?.avatar_urls?.["96"] || "",
      date: postsService.formatDate(post.date),
      excerpt: cleanExcerpt
    };
  };

  return (
    <>
      <Header />
      {/* Page Banner */}
      <PageBanner
        badge="BLOG"
        title="Legal Updates"
        backgroundImage={require('../../assets/images/gavel_banner.png')}
        breadcrumbs={[
          { label: 'Advocate Kannan', path: '/' },
          { label: 'Resources' },
          { label: 'Blog' }
        ]}
      />

      <main className="home-container">

        {/* Status Bar */}
        <div className="blog-status-bar">
          <span className="status-dot"></span>
          {searchTerm ? `Search results for: "${searchTerm}"` : "Live Blog Feed"}
        </div>

        {/* Posts + Sidebar Section */}
        <section className="main-content">
          <div className="posts-section">
            {loading ? (
              <div className="loading-state">Loading posts...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <>
                {posts.map((post) => {
                  const cardProps = transformPostData(post);
                  return <PostCard key={post.id} {...cardProps} />;
                })}
              </>
            )}

            {/* Pagination Placeholder */}
            {!loading && !error && posts.length > 0 && (
              <div style={{ textAlign: "center", margin: "20px 0" }}>
                <button className="pagination-btn">Load More</button>
              </div>
            )}
          </div>

          <aside className="sidebar-section">
            <Sidebar />
          </aside>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BlogPage;
